import React, { useEffect, useState, memo, useCallback } from 'react';
import styles from './SavedCreditCard.module.scss';
import SelectedPaymentCard from 'components/SelectedPaymentCard/SelectedPaymentCard';
import SavedCreditCardModal from '../SavedCreditCardModal/SavedCreditCardModal';
import { useAppDispatchContext, useAppStateContext } from 'stateProviders/useAppStateContext';
import { ACTION_TYPES } from 'stateProviders/appStateProvider';
import useUpdateUserDefaultCreditCard from '../SavedCreditCardModal/hooks/useUpdateUserDefaultCreditCard';
import useRemoveUserBillingAccount from '../SavedCreditCardModal/hooks/useRemoveUserBillingAccount';
import RemoveCardModal from 'components/RemoveCardModal/RemoveCardModal';
import { PrimaryIconButton } from 'components/Button/Button';
import CloseIcon from '@material-ui/icons/Close';

const SavedCreditCard = () => {
  const {
    userBillingAccounts: { billingAccounts, loading, defaultBillingAccount, selectedBillingAccount },
    paymentData: { isUseSavedCard },
  } = useAppStateContext();
  const [{ loading: isUpdating }, updateBillingAccount] = useUpdateUserDefaultCreditCard();
  const [{ loading: isRemoving, error: errorBillingAccount }, removeBillingAccount] = useRemoveUserBillingAccount();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const appDispatch = useAppDispatchContext();

  useEffect(() => {
    if (Array.isArray(billingAccounts) && billingAccounts.length) {
      const defaultBillingAccount = billingAccounts.find((item) => item.isDefault);

      if (defaultBillingAccount) {
        appDispatch({
          payload: { defaultBillingAccount },
          type: ACTION_TYPES.setUserBillingAccountsData,
        });
      } else {
        updateBillingAccount(billingAccounts[0].id);
      }
    }
  }, [appDispatch, billingAccounts, defaultBillingAccount, updateBillingAccount]);

  const data = selectedBillingAccount.id ? selectedBillingAccount : defaultBillingAccount;
  const cardIsNotDefined = !loading && !isUpdating && !data.id;

  useEffect(() => {
    if (isUseSavedCard && cardIsNotDefined) {
      setIsModalOpen(true);
    }
  }, [isUseSavedCard, cardIsNotDefined]);

  const onClickButton = useCallback(() => {
    setSelectedCardId(data.id);
    setIsModalOpen(true);
  }, [data]);

  const handleClose = useCallback(
    (isClosedOnUseCreditCard) => {
      setIsModalOpen(false);
      if (!isClosedOnUseCreditCard && cardIsNotDefined) {
        appDispatch({
          payload: false,
          type: ACTION_TYPES.setIsUseSavedCard,
        });
      }
    },
    [appDispatch, cardIsNotDefined]
  );

  const handleDeleteClick = useCallback(() => {
    setIsConfirmModal(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsConfirmModal(false);
  }, []);

  const onDeleteCard = useCallback(() => {
    if (data.id) {
      appDispatch({
        payload: false,
        type: ACTION_TYPES.setIsUseSavedCard,
      });
      removeBillingAccount(data.id).then(() => {
        onCloseModal();
      });
    }
  }, [data.id, removeBillingAccount, appDispatch, onCloseModal]);

  return (
    <>
      {!loading && !isUpdating && data.id && (
        <div className={styles.cardContainer}>
          <SelectedPaymentCard onClick={onClickButton}>
            <div className={styles.innerBlock}>
              <div className={styles.cardNumber}>{`XXXX-XXXX-XXXX-${data.cardNumber}`}</div>
              <div className={styles.nameOnCard}>{data.name}</div>
              <div className={styles.cardType}>{data.cardType}</div>
            </div>
            <div className={styles.deleteButton}>
              <PrimaryIconButton onClick={handleDeleteClick}>
                <CloseIcon />
              </PrimaryIconButton>
            </div>
          </SelectedPaymentCard>
        </div>
      )}
      <SavedCreditCardModal handleClose={handleClose} defaultSelectedCard={selectedCardId} isOpen={isModalOpen} />
      <RemoveCardModal
        open={isConfirmModal}
        disabled={isRemoving}
        loading={isRemoving}
        onChange={onCloseModal}
        onSubmit={onDeleteCard}
        onCancel={onCloseModal}
      />
    </>
  );
};

const MemoizedSavedCreditCard = memo(SavedCreditCard);

export default MemoizedSavedCreditCard;
